import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainNavbar } from '../../components/navigation/MainNavbar';
import { usePublicAuth } from '../../contexts/PublicAuthContext.jsx';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import ReviewModal from '../../components/reviews/ReviewModal';
import { hasExistingReview } from '../../services/reviewsService';
import { useAppDispatch } from '../../store/hooks';
import { openOrCreateConversationByUserId } from '../../store/chatThunks';
import {
  MessageCircle,
  CalendarDays,
  MapPin,
  Box,
  CheckCircle,
  XCircle,
  Clock,
  Package,
  User,
  ArrowLeft,
  DollarSign,
  AlertTriangle,
  Star,
  MessageSquare,
} from 'lucide-react';
import airportImage from '../../assets/airportt.png';

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
}) => {
  if (!isOpen) return null;

  const iconColors = {
    warning: 'text-yellow-500 dark:text-yellow-400',
    danger: 'text-red-500 dark:text-red-400',
    success: 'text-green-500 dark:text-green-400',
    info: 'text-blue-500 dark:text-blue-400',
  };

  const buttonColors = {
    warning:
      'bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-600',
    danger: 'bg-red-500 hover:bg-red-600 dark:bg-red-500 dark:hover:bg-red-600',
    success:
      'bg-green-500 hover:bg-green-600 dark:bg-green-500 dark:hover:bg-green-600',
    info: 'bg-blue-500 hover:bg-blue-600 dark:bg-blue-500 dark:hover:bg-blue-600',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black bg-opacity-50 dark:bg-opacity-70"
        onClick={onClose}
      />
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 border border-gray-200 dark:border-slate-800">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 ${iconColors[type]}`}>
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-6">
              {message}
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-xl border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-700 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-700 transition"
              >
                {cancelText}
              </button>
              <button
                onClick={() => {
                  onConfirm();
                  onClose();
                }}
                className={`px-4 py-2 rounded-xl ${buttonColors[type]} text-white font-semibold transition`}
              >
                {confirmText}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Success/Error Toast Component
const Toast = ({ isOpen, onClose, message, type = 'success' }) => {
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const bgColors = {
    success:
      'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900/50',
    error: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/50',
    info: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/50',
  };

  const textColors = {
    success: 'text-green-700 dark:text-green-400',
    error: 'text-red-700 dark:text-red-400',
    info: 'text-blue-700 dark:text-blue-400',
  };

  const icons = {
    success: <CheckCircle className="h-5 w-5" />,
    error: <XCircle className="h-5 w-5" />,
    info: <AlertTriangle className="h-5 w-5" />,
  };

  return (
    <div className="fixed top-24 right-4 z-50 animate-slide-in">
      <div
        className={`${bgColors[type]} ${textColors[type]} border rounded-xl p-4 shadow-lg flex items-center gap-3 max-w-md`}
      >
        {icons[type]}
        <p className="font-semibold flex-1">{message}</p>
        <button
          onClick={onClose}
          className="text-current opacity-70 hover:opacity-100"
        >
          <XCircle className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const formatDate = date => {
  if (!date) return 'N/A';
  let dateObj;
  if (typeof date === 'number') {
    dateObj = new Date(date);
  } else if (date && typeof date.toDate === 'function') {
    dateObj = date.toDate();
  } else if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
  } else {
    return 'N/A';
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatTime = time => {
  if (!time) return 'N/A';
  if (typeof time === 'string') {
    return time;
  }
  if (time instanceof Date) {
    return time.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }
  return 'N/A';
};

const getStatusIcon = status => {
  switch (status) {
    case 'pending':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'accepted':
      return <CheckCircle className="h-4 w-4 text-blue-500" />;
    case 'rejected':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'completed':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'delivered':
      return <Package className="h-4 w-4 text-purple-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
};

const getStatusColor = status => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/20 dark:border-yellow-800 dark:text-yellow-300';
    case 'accepted':
      return 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300';
    case 'rejected':
      return 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300';
    case 'completed':
      return 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300';
    case 'delivered':
      return 'bg-purple-50 border-purple-200 text-purple-800 dark:bg-purple-900/20 dark:border-purple-800 dark:text-purple-300';
    default:
      return 'bg-gray-50 border-gray-200 text-gray-800 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300';
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'pending':
      return t('myRequests.status.pending');
    case 'accepted':
      return t('myRequests.status.accepted');
    case 'rejected':
      return t('myRequests.status.rejected');
    case 'completed':
      return t('myRequests.status.completed');
    case 'delivered':
      return t('myRequests.status.delivered');
    default:
      return status?.toUpperCase() || 'UNKNOWN';
  }
};

export default function SenderTripRequests() {
  const { t } = useTranslation();
  const { tripId: rawTripId } = useParams();
  const navigate = useNavigate();
  const { user, initializing } = usePublicAuth();
  const dispatch = useAppDispatch();
  const [trip, setTrip] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    type: '',
    requestId: null,
    action: null,
  });
  const [toast, setToast] = useState({
    isOpen: false,
    message: '',
    type: 'success',
  });
  const [reviewModal, setReviewModal] = useState({
    visible: false,
    reviewedUserId: null,
    reviewedUserName: '',
    requestId: null,
    role: 'sender',
  });
  const [reviewStatuses, setReviewStatuses] = useState({});

  // Clean tripId from URL encoding issues
  const tripId = rawTripId
    ? String(rawTripId).replace(/--+$/, '').trim()
    : null;

  useEffect(() => {
    if (!tripId || !user?.uid) {
      setLoading(false);
      return;
    }

    // Load trip
    const loadTrip = async () => {
      try {
        const tripDoc = await getDoc(doc(db, 'trips', tripId));
        if (tripDoc.exists()) {
          const tripData = { id: tripDoc.id, ...tripDoc.data() };
          setTrip(tripData);

          // If trip type is carrying, redirect to traveler trip requests page
          // This page is for shipping trips (sender's trips)
          if (tripData.tripType === 'carrying') {
            navigate(`/trip-requests/${tripId}`, { replace: true });
            return;
          }
        } else {
          setToast({ isOpen: true, message: t('senderTripRequests.tripNotFound'), type: 'error' });
          setTimeout(() => navigate('/my-listings'), 2000);
        }
      } catch (error) {
        console.error('Error loading trip:', error);
        setToast({
          isOpen: true,
          message: t('senderTripRequests.failedToLoadTrip'),
          type: 'error',
        });
        setTimeout(() => navigate('/my-listings'), 2000);
      }
    };

    loadTrip();

    // Set up real-time listener for requests
    const requestsQuery = query(
      collection(db, 'requests'),
      where('tripId', '==', tripId),
    );

    const unsubscribe = onSnapshot(
      requestsQuery,
      snapshot => {
        const requestsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRequests(requestsData);
        setLoading(false);
      },
      error => {
        console.error('Error loading requests:', error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [tripId, user?.uid, navigate]);

  const getTravelerInfo = request => {
    return {
      id:
        request.requesterId ||
        request.travelerId ||
        request.requesterUid ||
        request.requesterUID ||
        request.userId,
      name:
        request.requesterName ||
        request.travelerName ||
        request.createdByName ||
        request.createdByEmail?.split('@')[0] ||
        t('common.unknownUser'),
    };
  };

  const handleOpenReviewModal = async request => {
    if (!user?.uid) {
      alert(t('senderTripRequests.pleaseLogInToReview'));
      return;
    }

    const travelerInfo = getTravelerInfo(request);
    if (!travelerInfo.id) {
      alert(t('senderTripRequests.travelerInfoMissing'));
      return;
    }

    try {
      const alreadyReviewed = await hasExistingReview(
        travelerInfo.id,
        request.id,
        user.uid,
      );
      if (alreadyReviewed) {
        setReviewStatuses(prev => ({ ...prev, [request.id]: true }));
        alert(t('senderTripRequests.alreadyReviewed'));
        return;
      }

      setReviewModal({
        visible: true,
        reviewedUserId: travelerInfo.id,
        reviewedUserName: travelerInfo.name,
        requestId: request.id,
        role: 'sender',
      });
    } catch (error) {
      console.error('Error opening review modal:', error);
      alert(error.message || t('senderTripRequests.failedToOpenReviewModal'));
    }
  };

  const handleReviewSuccess = requestId => {
    setReviewStatuses(prev => ({ ...prev, [requestId]: true }));
  };

  const handleStartChat = async (targetUserId, targetUserName) => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!targetUserId) {
      navigate('/chat');
      return;
    }

    try {
      const conversation = await dispatch(
        openOrCreateConversationByUserId(
          targetUserId,
          targetUserName || 'Traveler',
        ),
      );
      if (conversation?.id) {
        navigate(`/chat/${conversation.id}`);
      } else {
        navigate('/chat');
      }
    } catch (error) {
      console.error('Error opening chat:', error);
      navigate('/chat');
    }
  };

  useEffect(() => {
    if (!user?.uid || requests.length === 0) {
      setReviewStatuses({});
      return;
    }

    let isMounted = true;

    const fetchStatuses = async () => {
      try {
        const entries = await Promise.all(
          requests.map(async request => {
            const travelerInfo = getTravelerInfo(request);
            if (!travelerInfo.id) {
              return [request.id, false];
            }
            try {
              const exists = await hasExistingReview(
                travelerInfo.id,
                request.id,
                user.uid,
              );
              return [request.id, exists];
            } catch (error) {
              console.error('Error checking review status:', error);
              return [request.id, false];
            }
          }),
        );

        if (!isMounted) return;
        const statusMap = {};
        entries.forEach(([id, status]) => {
          if (id) statusMap[id] = status;
        });
        setReviewStatuses(statusMap);
      } catch (error) {
        console.error('Error loading review statuses:', error);
      }
    };

    fetchStatuses();

    return () => {
      isMounted = false;
    };
  }, [requests, user?.uid]);

  const handleAcceptRequest = async requestId => {
    if (processingRequest) return;
    setConfirmationModal({
      isOpen: true,
      type: 'warning',
      requestId,
      action: async () => {
        setProcessingRequest(requestId);
        try {
          // Accept the selected request
          const requestRef = doc(db, 'requests', requestId);
          await updateDoc(requestRef, {
            status: 'accepted',
            updatedAt: new Date(),
          });

          // Reject all other pending requests for the same trip
          const otherPendingRequests = requests.filter(
            req => req.id !== requestId && req.status === 'pending',
          );

          if (otherPendingRequests.length > 0) {
            const rejectPromises = otherPendingRequests.map(req =>
              updateDoc(doc(db, 'requests', req.id), {
                status: 'rejected',
                updatedAt: new Date(),
              }),
            );
            await Promise.all(rejectPromises);
          }

          setToast({
            isOpen: true,
            message: otherPendingRequests.length > 0
              ? t('senderTripRequests.requestAcceptedSuccessfully') + ' ' + t('senderTripRequests.otherRequestsAutomaticallyRejected', { count: otherPendingRequests.length })
              : t('senderTripRequests.requestAcceptedSuccessfully'),
            type: 'success',
          });
        } catch (error) {
          console.error('Error accepting request:', error);
          setToast({
            isOpen: true,
            message: t('senderTripRequests.failedToAcceptRequest'),
            type: 'error',
          });
        } finally {
          setProcessingRequest(null);
        }
      },
    });
  };

  const handleRejectRequest = async requestId => {
    if (processingRequest) return;
    setConfirmationModal({
      isOpen: true,
      type: 'danger',
      requestId,
      action: async () => {
        setProcessingRequest(requestId);
        try {
          const requestRef = doc(db, 'requests', requestId);
          await updateDoc(requestRef, {
            status: 'rejected',
            updatedAt: new Date(),
          });
          setToast({
            isOpen: true,
            message: t('senderTripRequests.requestRejected'),
            type: 'success',
          });
        } catch (error) {
          console.error('Error rejecting request:', error);
          setToast({
            isOpen: true,
            message: t('senderTripRequests.failedToRejectRequest'),
            type: 'error',
          });
        } finally {
          setProcessingRequest(null);
        }
      },
    });
  };

  const handleMarkAsDelivered = async requestId => {
    if (processingRequest) return;
    setProcessingRequest(requestId);
    try {
      const requestRef = doc(db, 'requests', requestId);
      await updateDoc(requestRef, {
        status: 'delivered',
        updatedAt: new Date(),
      });
      setToast({
        isOpen: true,
        message: t('senderTripRequests.itemMarkedAsDelivered'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error marking as delivered:', error);
      setToast({
        isOpen: true,
        message: t('senderTripRequests.failedToMarkAsDelivered'),
        type: 'error',
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleOpenPaymentModal = request => {
    const rawPrice = request?.price ?? request?.estimatedPrice ?? 0;
    const currency = request?.currency || trip?.currency || 'USD';
    const costNum =
      typeof rawPrice === 'string'
        ? parseFloat(String(rawPrice).replace(/[^0-9.]/g, ''))
        : Number(rawPrice);
    const fee = !isNaN(costNum) ? costNum * 0.15 : 0;
    const total = !isNaN(costNum) ? costNum + fee : 0;
    setPaymentModalData({
      requestId: request.id,
      currency,
      subtotal: costNum,
      fee,
      total,
    });
    setShowPaymentModal(true);
  };

  const handleConfirmPayment = async () => {
    if (!paymentModalData) return;
    setProcessingRequest(paymentModalData.requestId);
    try {
      const requestRef = doc(db, 'requests', paymentModalData.requestId);
      await updateDoc(requestRef, {
        status: 'completed',
        updatedAt: new Date(),
      });
      setShowPaymentModal(false);
      setPaymentModalData(null);
      setToast({
        isOpen: true,
        message: t('senderTripRequests.paymentConfirmed'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error confirming payment:', error);
      setToast({
        isOpen: true,
        message: t('senderTripRequests.failedToConfirmPayment'),
        type: 'error',
      });
    } finally {
      setProcessingRequest(null);
    }
  };

  const renderRequestCard = request => {
    const travelerInfo = getTravelerInfo(request);
    const canAccept = request.status === 'pending';
    const canReject = request.status === 'pending';

    return (
      <div className="group relative rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800 hover:shadow-xl hover:border-gray-300 dark:hover:border-slate-700 transition-all duration-300">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 pb-4 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-black dark:bg-slate-700 flex items-center justify-center">
              <User className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white truncate">
                {request.requesterName ||
                  request.createdByName ||
                  t('common.unknownUser')}
              </h3>
              {request.requesterEmail && (
                <p className="text-xs text-gray-600 dark:text-slate-400 truncate">
                  {request.requesterEmail}
                </p>
              )}
              {request.createdAt && (
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                  {formatDate(request.createdAt)}
                </p>
              )}
            </div>
          </div>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${getStatusColor(
              request.status,
            )} flex-shrink-0`}
          >
            {getStatusIcon(request.status)}
            <span className="text-xs font-bold">
              {getStatusLabel(request.status, t)}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-3 mb-4">
          {(request.itemTitle || request.itemDescription) && (
            <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5">
                {t('senderTripRequests.item')}
              </p>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-relaxed line-clamp-3">
                {request.itemTitle || request.itemDescription || 'N/A'}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {(request.weight ||
              request.spaceNeeded ||
              request.availableSpace) && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Box className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <p className="text-xs font-semibold uppercase text-blue-700 dark:text-blue-300">
                    {t('senderTripRequests.spaceNeeded')}
                  </p>
                </div>
                <p className="text-sm font-bold text-blue-900 dark:text-blue-200">
                  {request.weight ||
                    request.spaceNeeded ||
                    request.availableSpace}{' '}
                  {request.spaceUnit || trip?.spaceUnit || 'kg'}
                </p>
              </div>
            )}

            {(request.price || request.estimatedPrice) && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-xs font-semibold uppercase text-yellow-700 dark:text-yellow-300">
                    {t('senderTripRequests.price')}
                  </p>
                </div>
                <p className="text-sm font-bold text-yellow-900 dark:text-yellow-200">
                  {request.currency || trip?.currency || 'USD'}{' '}
                  {request.price || request.estimatedPrice || 'N/A'}
                </p>
              </div>
            )}
          </div>

          {request.notes && (
            <div className="p-3 bg-gray-50 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5">
                {t('senderTripRequests.notes')}
              </p>
              <p className="text-sm text-gray-700 dark:text-slate-300 leading-relaxed line-clamp-2">
                {request.notes}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        {request.status === 'pending' && (
          <div className="space-y-3 pt-4 border-t border-gray-100 dark:border-slate-700">
            <p className="text-sm text-gray-700 dark:text-slate-300 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl px-4 py-3">
              {t('senderTripRequests.theSenderRequests', { item: request.itemTitle || request.itemDescription || t('senderTripRequests.thisItem') })}
            </p>
            <div className="flex gap-3">
              {canAccept && (
                <button
                  onClick={() => handleAcceptRequest(request.id)}
                  disabled={processingRequest === request.id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="h-5 w-5" />
                  {t('senderTripRequests.accept')}
                </button>
              )}
              {canReject && (
                <button
                  onClick={() => handleRejectRequest(request.id)}
                  disabled={processingRequest === request.id}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle className="h-5 w-5" />
                  {t('senderTripRequests.reject')}
                </button>
              )}
              <button
                onClick={() => {
                  const travelerInfo = getTravelerInfo(request);
                  handleStartChat(travelerInfo.id, travelerInfo.name);
                }}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/profile/support')}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageSquare className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {request.status === 'accepted' && (
          <div className="pt-4 border-t border-gray-100 dark:border-slate-700 space-y-3">
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                {t('senderTripRequests.requestAccepted')}
              </p>
            </div>
            <button
              onClick={() => handleMarkAsDelivered(request.id)}
              disabled={processingRequest === request.id}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Package className="h-5 w-5" />
              {t('senderTripRequests.markAsDelivered')}
            </button>
            <div className="flex gap-3">
              <button
                onClick={() =>
                  handleStartChat(travelerInfo.id, travelerInfo.name)
                }
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageCircle className="h-5 w-5" />
                {t('senderTripRequests.contactTraveler')}
              </button>
              <button
                onClick={() => navigate('/profile/support')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageSquare className="h-5 w-5" />
                {t('profile.supportTitle') || 'Support'}
              </button>
            </div>
          </div>
        )}

        {request.status === 'delivered' && (
          <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mb-3 border border-green-200 dark:border-green-800">
              <p className="text-sm font-semibold text-green-900 dark:text-green-300">
                {t('senderTripRequests.confirmReceivedApprovalPending')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => {
                  if (!reviewStatuses[request.id]) {
                    handleOpenReviewModal(request);
                  }
                }}
                disabled={reviewStatuses[request.id]}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                  reviewStatuses[request.id]
                    ? 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-400 cursor-not-allowed'
                    : 'bg-yellow-500 text-black hover:bg-yellow-400'
                }`}
              >
                <Star className="h-4 w-4" />
                {reviewStatuses[request.id] ? t('senderTripRequests.reviewed') : t('senderTripRequests.rate')}
              </button>
            </div>
          </div>
        )}

        {request.status === 'completed' && (
          <div className="pt-4 border-t border-gray-100 dark:border-slate-700">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mb-3 border border-green-200 dark:border-green-800">
              <p className="text-sm font-semibold text-green-900 dark:text-green-300">
                {t('senderTripRequests.requestCompletedSuccessfully')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mb-3">
              <button
                onClick={() => {
                  if (!reviewStatuses[request.id]) {
                    handleOpenReviewModal(request);
                  }
                }}
                disabled={reviewStatuses[request.id]}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all duration-200 shadow-sm ${
                  reviewStatuses[request.id]
                    ? 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-slate-400 cursor-not-allowed'
                    : 'bg-yellow-500 text-black hover:bg-yellow-400'
                }`}
              >
                <Star className="h-4 w-4" />
                {reviewStatuses[request.id] ? t('senderTripRequests.reviewed') : t('senderTripRequests.rate')}
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const travelerInfo = getTravelerInfo(request);
                  handleStartChat(travelerInfo.id, travelerInfo.name);
                }}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageCircle className="h-5 w-5" />
                {t('senderTripRequests.contact')}
              </button>
              <button
                onClick={() => navigate('/profile/support')}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200"
              >
                <MessageSquare className="h-5 w-5" />
                {t('profile.supportTitle') || 'Support'}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const isLoading = initializing || loading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />

        {/* Hero Section Skeleton */}
        <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="h-16 bg-white/20 dark:bg-slate-700/50 rounded-xl mb-4 w-3/4 animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-full animate-pulse"></div>
                <div className="h-6 bg-white/20 dark:bg-slate-700/50 rounded-lg mb-2 w-5/6 animate-pulse"></div>
              </div>
              <div className="relative">
                <div className="w-full h-80 bg-white/20 dark:bg-slate-700/50 rounded-2xl animate-pulse"></div>
              </div>
            </div>
          </div>
        </section>

        <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
          {/* Back Button Skeleton */}
          <div className="h-10 bg-gray-200 dark:bg-slate-800 rounded-lg w-64 mb-6 animate-pulse"></div>

          {/* Trip Info Card Skeleton */}
          <div className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-lg border border-gray-200 dark:border-slate-800 mb-8 animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4"></div>
            <div className="h-32 bg-gray-200 dark:bg-slate-800 rounded-xl"></div>
          </div>

          {/* Requests Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-200 dark:bg-slate-800 rounded-lg w-48 mb-4 animate-pulse"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-md border border-gray-200 dark:border-slate-800"
              >
                <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded-lg w-3/4 mb-4 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-full mb-2 animate-pulse"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded-lg w-5/6 mb-4 animate-pulse"></div>
                <div className="h-20 bg-gray-200 dark:bg-slate-800 rounded-xl mb-4 animate-pulse"></div>
                <div className="h-12 bg-gray-200 dark:bg-slate-800 rounded-xl animate-pulse"></div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
        <MainNavbar />
        <main className="mx-auto max-w-6xl px-4 pt-28 pb-16 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white dark:bg-slate-900 p-8 shadow-lg text-center border border-gray-200 dark:border-slate-800">
            <p className="text-gray-600 dark:text-slate-400 mb-4">
              {t('senderTripRequests.tripNotFound')}
            </p>
            <button
              onClick={() => navigate('/my-listings')}
              className="inline-flex rounded-2xl bg-black dark:bg-yellow-500 px-5 py-3 text-sm font-semibold text-yellow-400 dark:text-black transition hover:bg-gray-900 dark:hover:bg-yellow-400"
            >
              {t('senderTripRequests.backToMyListings')}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const acceptedCount = requests.filter(r => r.status === 'accepted').length;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      <MainNavbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 dark:from-slate-800 dark:via-slate-900 dark:to-black overflow-hidden pt-28 pb-16 min-h-[400px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-4 drop-shadow-lg">
                {t('senderTripRequests.tripRequests')}
              </h1>
              <p className="text-xl text-yellow-50 mb-8 max-w-2xl">
                {t('senderTripRequests.manageRequestsForListing')}
              </p>
            </div>
            <div className="relative">
              <img
                src={airportImage}
                alt="Airport"
                className="w-full h-auto object-contain"
                style={{ maxHeight: '350px' }}
              />
            </div>
          </div>
        </div>

        {/* Decorative bottom gradient */}
        <div
          className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white/20 to-transparent"
          style={{ zIndex: 1 }}
        ></div>
      </section>

      <main className="mx-auto max-w-6xl px-4 pt-16 pb-16 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/my-listings')}
          className="flex items-center gap-2 mb-6 text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white transition"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="font-semibold">{t('senderTripRequests.backToMyListings')}</span>
        </button>

        {/* Trip Info Card */}
        <div className="rounded-2xl bg-gradient-to-br from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 p-6 shadow-lg border border-gray-200 dark:border-slate-800 mb-8">
          <div className="flex items-start gap-4 mb-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <MapPin className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-2">
                {t('senderTripRequests.route')}
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                <span className="block">
                  {trip.originDetails?.city || trip.originCity || t('home.from')},{' '}
                  {trip.originDetails?.country || trip.originCountry || ''}
                </span>
                <span className="text-gray-400 dark:text-slate-500 mx-2 my-1 block">
                  ↓
                </span>
                <span className="block">
                  {trip.destinationDetails?.city ||
                    trip.destinationCity ||
                    t('home.to')}
                  ,{' '}
                  {trip.destinationDetails?.country ||
                    trip.destinationCountry ||
                    ''}
                </span>
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <CalendarDays className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {t('senderTripRequests.departure')}
                </p>
                <p className="text-sm font-bold text-gray-900 dark:text-white">
                  {formatDate(trip.departureDate)} {t('common.at')} {' '}
                  {formatTime(trip.departureTime)}
                </p>
              </div>
            </div>

            {trip.itemDescription && (
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Package className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold uppercase text-gray-500 dark:text-slate-400 mb-1">
                    {t('senderTripRequests.itemDescription')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white line-clamp-2">
                    {trip.itemDescription}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Requests Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white">
            {t('senderTripRequests.requests')}{' '}
            <span className="text-gray-500 dark:text-slate-400">
              ({requests.length})
            </span>
          </h2>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {pendingCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('senderTripRequests.pending')}
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-bold text-gray-900 dark:text-white">
                {acceptedCount}
              </span>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {t('senderTripRequests.accepted')}
              </span>
            </div>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="rounded-2xl bg-white dark:bg-slate-900 p-12 shadow-lg border-2 border-gray-200 dark:border-slate-800 text-center">
            <MessageCircle className="h-16 w-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-slate-400">
              {t('senderTripRequests.noRequestsYetForTrip')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...requests]
              .sort((a, b) => {
                const aTime = a.createdAt?.toDate
                  ? a.createdAt.toDate().getTime()
                  : a.createdAt
                  ? new Date(a.createdAt).getTime()
                  : 0;
                const bTime = b.createdAt?.toDate
                  ? b.createdAt.toDate().getTime()
                  : b.createdAt
                  ? new Date(b.createdAt).getTime()
                  : 0;
                return bTime - aTime;
              })
              .map(request => (
                <div key={request.id}>{renderRequestCard(request)}</div>
              ))}
          </div>
        )}
      </main>

      {/* Payment Modal */}
      {showPaymentModal && paymentModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800">
            <h3 className="text-2xl font-black text-gray-900 dark:text-white mb-6 text-center">
              {t('senderTripRequests.confirmPayment')}
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('senderTripRequests.subtotal')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {paymentModalData.subtotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-slate-700">
                <span className="text-gray-600 dark:text-slate-400">
                  {t('senderTripRequests.fees15')}
                </span>
                <span className="font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency} {paymentModalData.fee.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {t('senderTripRequests.total')}
                </span>
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  {paymentModalData.currency}{' '}
                  {paymentModalData.total.toFixed(2)}
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentModalData(null);
                }}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-slate-700 transition"
              >
                {t('senderTripRequests.cancel')}
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={processingRequest === paymentModalData.requestId}
                className="flex-1 px-4 py-3 bg-black dark:bg-yellow-500 text-white dark:text-black rounded-xl font-bold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
              >
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() =>
          setConfirmationModal({
            isOpen: false,
            type: '',
            requestId: null,
            action: null,
          })
        }
        onConfirm={confirmationModal.action || (() => {})}
        title={
          confirmationModal.type === 'danger'
            ? t('senderTripRequests.rejectRequest')
            : t('senderTripRequests.acceptRequest')
        }
        message={
          confirmationModal.type === 'danger'
            ? t('senderTripRequests.areYouSureReject')
            : t('senderTripRequests.areYouSureAccept')
        }
        confirmText={confirmationModal.type === 'danger' ? t('senderTripRequests.reject') : t('senderTripRequests.accept')}
        cancelText={t('senderTripRequests.cancel')}
        type={confirmationModal.type || 'warning'}
      />

      {/* Toast Notification */}
      <Toast
        isOpen={toast.isOpen}
        onClose={() =>
          setToast({ isOpen: false, message: '', type: 'success' })
        }
        message={toast.message}
        type={toast.type}
      />
      <ReviewModal
        visible={reviewModal.visible}
        reviewedUserId={reviewModal.reviewedUserId}
        reviewedUserName={reviewModal.reviewedUserName}
        requestId={reviewModal.requestId}
        userRole={reviewModal.role}
        onClose={() =>
          setReviewModal(prev => ({
            ...prev,
            visible: false,
          }))
        }
        onSubmitSuccess={() => {
          if (reviewModal.requestId) {
            handleReviewSuccess(reviewModal.requestId);
          }
        }}
      />
    </div>
  );
}
